import React from "react";
import { useState, useEffect } from "react";
import './admin.css';
import { Link, useNavigate } from "react-router-dom";
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrashCan, faCircleCheck, faBan,
  faMagnifyingGlass, faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';


const avatarColors = [
  "#FF4820", "#6366f1", "#8b5cf6", "#ec4899",
  "#14b8a6", "#f59e0b", "#10b981", "#3b82f6"
];

const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const getAvatarColor = (name) => {
  if (!name) return avatarColors[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return avatarColors[Math.abs(hash) % avatarColors.length];
};


const Admin = ({ isLoggedIn, loginUser, onLogout }) => {
  const [userList, setUserList] = useState([]);
  const [deleteInProcess, setDeleteInProcess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(["pending"]);
  const [sortBy, setSortBy] = useState("newest");
  const [confirmAction, setConfirmAction] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const getAllUsers = async () => {
      try {
        const response = await axios.get(`/api/v1/user/`);
        setUserList(response.data.userList);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          onLogout();
          navigate('/login');
        } else if (error.response) {
          setError(error.response.data.message);
        } else {
          setError('An error occurred while fetching users');
        }
      } finally {
        setLoading(false);
      }
    };

    getAllUsers();
  }, [onLogout, navigate]);

  if (!isLoggedIn || loginUser.role !== 'admin') {
    navigate('/');
    return null;
  }

  const onBtnClickDeleteUser = async (userID) => {
    try {
      setDeleteInProcess(userID);

      await axios.post(`/api/v1/user/deleteUser/${userID}?_method=PUT`);

      setUserList((prev) => prev.filter((u) => u._id !== userID));
    } catch (error) {
      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else if (error.response) {
        setError(error.response.data.message);
      } else {
        setError('An error occurred while deleting user');
      }
    } finally {
      setDeleteInProcess(null);
      setConfirmAction(null);
    }
  };

  const onBtnClickApproveUser = async (userID) => {
    try {
      setUserList((prevList) =>
        prevList.map((user) =>
          (user._id === userID)
          ? {...user, isApproved: true}
          : user
        )
      );

      const response = await axios.post(`/api/v1/user/approveUser/${userID}?_method=PUT`);

      setUserList((prevList) =>
        prevList.map((user) =>
          (user._id === userID)
          ? {...user, isApproved: response.data.updatedApproval}
          : user
        )
      );

    } catch (error) {
      setUserList((prevList) =>
        prevList.map((user) =>
          (user._id === userID)
          ? {...user, isApproved: false}
          : user
        )
      );

      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else if (error.response) {
        setError(error.response.data.message);
      } else {
        setError('An error occurred while approving user');
      }
    }
  };

  const handleToggleBan = async (userID) => {
    try {
      setUserList((prevList) =>
        prevList.map((user) =>
          (user._id === userID)
          ? {...user, isBanned: !user.isBanned}
          : user
        )
      );

      const response = await axios.post(`/api/v1/user/toggleBan/${userID}?_method=PUT`);

      setUserList((prevList) =>
        prevList.map((user) =>
          (user._id === userID)
          ? {...user, isBanned: response.data.updatedBan}
          : user
        )
      );

    } catch (error) {
      setUserList((prevList) =>
        prevList.map((user) =>
          (user._id === userID)
          ? {...user, isBanned: !user.isBanned}
          : user
        )
      );

      if (error.response && error.response.status === 401) {
        onLogout();
        navigate('/login');
      } else if (error.response) {
        setError(error.response.data.message);
      } else {
        setError('An error occurred while banning user');
      }
    }
  };

  const pendingCount = userList.filter((u) => !u.isApproved).length;
  const bannedCount = userList.filter((u) => u.isBanned).length;

  const approvedCount = userList.filter((u) => u.isApproved && !u.isBanned).length;

  const toggleFilter = (f) => {
    setFilters((prev) => prev.includes(f) ? prev.filter((v) => v !== f) : [...prev, f]);
  };

  const filteredUsers = userList
    .filter((user) => {
      const matchesSearch =
        user.userName?.toLowerCase().includes(search.toLowerCase()) ||
        user.email?.toLowerCase().includes(search.toLowerCase());
      if (!matchesSearch) return false;
      if (filters.length === 0) return true;
      if (filters.includes("pending") && !user.isApproved) return true;
      if (filters.includes("banned") && user.isBanned) return true;
      if (filters.includes("approved") && user.isApproved && !user.isBanned) return true;
      return false;
    })
    .sort((a, b) => {
      if (sortBy === "name-asc") return (a.userName || "").localeCompare(b.userName || "");
      if (sortBy === "name-desc") return (b.userName || "").localeCompare(a.userName || "");
      const rank = (u) => {
        if (!u.isApproved) return 0;
        if (u.isBanned) return 1;
        return 2;
      };
      if (sortBy === "oldest") return rank(a) - rank(b);
      return rank(a) - rank(b);
    });

  if (loading) {
    return (
      <div className="sr-admin">
        <div className="sr-admin__top">
          <div className="sr-admin__top-content">
            <h1 className="sr-admin__title">Admin Panel</h1>
            <p className="sr-admin__subtitle">Loading users...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sr-admin">
      <div className="sr-admin__top">
        <div className="sr-admin__top-content">
          <h1 className="sr-admin__title">Admin Panel</h1>
          <p className="sr-admin__subtitle">{userList.length} registered user{userList.length !== 1 ? "s" : ""}</p>

          <div className="sr-admin__search">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="sr-admin__search-icon" />
            <input
              type="text"
              className="sr-admin__search-input"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

        </div>
      </div>

      <div className="sr-admin__body">
        <div className="sr-admin__sort-bar">
          <span className="sr-admin__sort-label">
            Active filters: {filters.length > 0 ? <><strong>{filters.length}</strong> <button className="sr-admin__sort-clear" onClick={() => setFilters([])}>Clear</button></> : "None"}
          </span>
          <select className="sr-admin__sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="newest">Default</option>
            <option value="name-asc">Name A-Z</option>
            <option value="name-desc">Name Z-A</option>
          </select>
        </div>

        <div className="sr-admin__chips">
          <button
            className={`sr-admin__chip ${filters.includes("pending") ? "sr-admin__chip--active" : ""}`}
            onClick={() => toggleFilter("pending")}
          >
            Pending ({pendingCount})
          </button>
          <button
            className={`sr-admin__chip ${filters.includes("banned") ? "sr-admin__chip--active" : ""}`}
            onClick={() => toggleFilter("banned")}
          >
            Banned ({bannedCount})
          </button>
          <button
            className={`sr-admin__chip ${filters.includes("approved") ? "sr-admin__chip--active" : ""}`}
            onClick={() => toggleFilter("approved")}
          >
            Approved ({approvedCount})
          </button>
        </div>

        {error !== "" && <div className="sr-admin__error">{error}</div>}

        <div className="sr-admin__list">
          {filteredUsers.map((el) => (
            <div
              key={el._id}
              className={`sr-admin__card${deleteInProcess === el._id ? " sr-admin__card--deleting" : ""}${el.isBanned ? " sr-admin__card--banned" : ""}`}
            >
              <div className="sr-admin__card-header">
                <Link to={`/profile/${el._id}`} className="sr-admin__card-user">
                  {el.avatar ? (
                    <img src={el.avatar} alt={el.userName} className="sr-admin__avatar-img" />
                  ) : (
                    <div
                      className="sr-admin__avatar"
                      style={{ background: getAvatarColor(el.userName) }}
                    >
                      {getInitials(el.userName)}
                    </div>
                  )}
                  <div className="sr-admin__card-details">
                    <span className="sr-admin__card-name">{el.userName}</span>
                    {el.email && <span className="sr-admin__card-email">{el.email}</span>}
                  </div>
                </Link>
                <div className="sr-admin__card-badges">
                  {el.isApproved ? (
                    <span className="sr-admin__badge sr-admin__badge--approved">Approved</span>
                  ) : (
                    <span className="sr-admin__badge sr-admin__badge--pending">Pending</span>
                  )}
                  {el.isBanned && (
                    <span className="sr-admin__badge sr-admin__badge--banned">Banned</span>
                  )}
                </div>
              </div>

              <div className="sr-admin__card-actions">
                {!el.isApproved && (
                  <button
                    onClick={() => setConfirmAction({ type: "approve", userId: el._id, userName: el.userName })}
                    className="sr-admin__btn sr-admin__btn--approve"
                  >
                    <FontAwesomeIcon icon={faCircleCheck} /> Authorize
                  </button>
                )}
                {el.isBanned ? (
                  <button
                    onClick={() => setConfirmAction({ type: "unban", userId: el._id, userName: el.userName })}
                    className="sr-admin__btn sr-admin__btn--unban"
                  >
                    <FontAwesomeIcon icon={faCircleCheck} /> Unban
                  </button>
                ) : (
                  <button
                    onClick={() => setConfirmAction({ type: "ban", userId: el._id, userName: el.userName })}
                    className="sr-admin__btn sr-admin__btn--ban"
                  >
                    <FontAwesomeIcon icon={faBan} /> Ban
                  </button>
                )}
                <button
                  onClick={() => setConfirmAction({ type: "delete", userId: el._id, userName: el.userName })}
                  className="sr-admin__btn sr-admin__btn--delete"
                >
                  <FontAwesomeIcon icon={faTrashCan} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredUsers.length === 0 && (
          <div className="sr-admin__empty">
            <p className="sr-admin__empty-text">
              {search || filters.length > 0 ? "No users match your filters" : "No users found"}
            </p>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmAction && (() => {
        const configs = {
          approve: {
            icon: faCircleCheck,
            iconClass: "sr-admin__modal-icon--approve",
            title: "Authorize User?",
            text: `This will approve "${confirmAction.userName}" to use the platform. They will be able to create rides and book seats.`,
            btnClass: "sr-admin__modal-btn--approve",
            btnIcon: faCircleCheck,
            btnLabel: "Authorize",
            onConfirm: () => { onBtnClickApproveUser(confirmAction.userId); setConfirmAction(null); }
          },
          ban: {
            icon: faBan,
            iconClass: "sr-admin__modal-icon--ban",
            title: "Ban User?",
            text: `This will ban "${confirmAction.userName}" from the platform. They will not be able to create rides or book seats until unbanned.`,
            btnClass: "sr-admin__modal-btn--ban",
            btnIcon: faBan,
            btnLabel: "Ban User",
            onConfirm: () => { handleToggleBan(confirmAction.userId); setConfirmAction(null); }
          },
          unban: {
            icon: faCircleCheck,
            iconClass: "sr-admin__modal-icon--approve",
            title: "Unban User?",
            text: `This will remove the ban on "${confirmAction.userName}". They will regain full access to the platform.`,
            btnClass: "sr-admin__modal-btn--approve",
            btnIcon: faCircleCheck,
            btnLabel: "Unban User",
            onConfirm: () => { handleToggleBan(confirmAction.userId); setConfirmAction(null); }
          },
          delete: {
            icon: faTriangleExclamation,
            iconClass: "",
            title: "Delete User?",
            text: `This will remove "${confirmAction.userName}" from the platform. Their account will be soft-deleted and cannot be easily restored.`,
            btnClass: "sr-admin__modal-btn--confirm",
            btnIcon: faTrashCan,
            btnLabel: "Delete",
            onConfirm: () => onBtnClickDeleteUser(confirmAction.userId)
          }
        };
        const cfg = configs[confirmAction.type];
        return (
          <>
            <div className="sr-admin__modal-overlay" onClick={() => setConfirmAction(null)} />
            <div className="sr-admin__modal">
              <div className={`sr-admin__modal-icon ${cfg.iconClass}`}>
                <FontAwesomeIcon icon={cfg.icon} />
              </div>
              <h3 className="sr-admin__modal-title">{cfg.title}</h3>
              <p className="sr-admin__modal-text">{cfg.text}</p>
              <div className="sr-admin__modal-actions">
                <button
                  className="sr-admin__modal-btn sr-admin__modal-btn--cancel"
                  onClick={() => setConfirmAction(null)}
                >
                  Cancel
                </button>
                <button
                  className={`sr-admin__modal-btn ${cfg.btnClass}`}
                  onClick={cfg.onConfirm}
                >
                  <FontAwesomeIcon icon={cfg.btnIcon} /> {cfg.btnLabel}
                </button>
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
};

export default Admin;
